import React, { Key, useState } from 'react';
import { useAsyncList } from '@react-stately/data';
import { MenuTriggerAction } from '@react-types/combobox';
import { LoadingState } from '@react-types/shared';
import cn from 'classnames';

import { useInstitute } from '../../hooks/useInstitutes';
import { useGetInstitutesMutation } from '../../hooks/useQueryClientInstitutesHook';
import {
  getInstituteIdFromInstituteName,
  getInstituteNameFromInstituteId
} from '../../utils/instituteUtils';

import { ComboBox } from '../ComboboxWithInitialValue';
import {
  AsyncListLoadFunction,
  Institute,
  OptionsType,
  OverlayOptions
} from '../types';

export interface InstituteComboBoxProps {
  label: string;
  options?: Array<OptionsType>;
  onSelectionChange?: (e: Key) => void;
  selectedKey?: string;
  placeholder?: string;
  renderListItem?: any;
  onBlur?: (e: React.FocusEvent) => void;
  onOpenChange?: (
    isOpen: boolean,
    menuTrigger?: MenuTriggerAction | undefined
  ) => void;
  error?: string;
  fieldContainerStyles?: string;
  wrapperWithPopOverStyles?: string;
  inputGroupStyles?: string;
  inputFieldStyles?: string;
  buttonStyles?: string;
  errorMessageStyles?: string;
  popOverStyles?: string;
  listBoxStyles?: string;
  dropDownIcon?: any;
  selectedOptionIcon?: any;
  defaultFilter?: any;
  isDisabled?: boolean;
  apiStatus?: string;
  autoFocus?: boolean;
  listItemStyles?: string;
  itemContentStyles?: string;
  loadingState?: LoadingState;
  onLoadMore?: () => any;
  overlayOptions?: OverlayOptions;
  getListItemTextValue?: (T: any) => any;
  initialInstituteValue?: string;
  mobilePlaceholder?: string;
  enableDataMask?: boolean;
  hintClassName?: string;
}

export const InstitutesComboBox = (props: InstituteComboBoxProps) => {
  const {
    onOpenChange,
    onSelectionChange,
    selectedKey,
    inputFieldStyles = '',
    buttonStyles = '',
    placeholder = '',
    error,
    initialInstituteValue,
    mobilePlaceholder,
    enableDataMask,
    hintClassName,
    ...others
  } = props;
  const [previousFilteredText, setPreviousFilteredText] = useState(undefined);

  const { institutes } = useInstitute();

  const onSelectionChangeFunc = (key) => {
    if (key) {
      onSelectionChange && onSelectionChange(key);
    }
  };

  const onOpenChangeFunc = (key: any) => {
    onOpenChange && onOpenChange(key);
  };

  const onInputChangeFunc = (value) => {
    setPreviousFilteredText(value);
    if (value === '') {
      onSelectionChange('');
    }
  };

  const { getInstitutesApi } = useGetInstitutesMutation();

  const loadItems: AsyncListLoadFunction<
    { institute_id: string; institute_name: any; institute_type: string },
    any
  > = async ({ signal, cursor, filterText, selectedKeys, items }) => {
    let previousInstitutes = institutes.current ? institutes.current : [];
    if (previousInstitutes && previousInstitutes.length > 0) {
      previousInstitutes = previousInstitutes.filter(
        (eachInstitute) => eachInstitute.institute_id !== 'Other'
      );
    }

    let json = { institutes: [...previousInstitutes] };

    if (previousFilteredText !== filterText) {
      const isInstitutePresentInPreviousInstitutes = previousInstitutes.filter(
        (eachInstitute) => eachInstitute.institute_name === filterText
      );

      if (isInstitutePresentInPreviousInstitutes.length > 0) {
        json = {
          institutes: [...isInstitutePresentInPreviousInstitutes]
        };
      } else {
        json = await getInstitutesApi({ filterText });
        if (json.institutes && json.institutes.length > 0) {
          const excludeInstitutes = [...json.institutes];

          json.institutes = excludeInstitutes.filter(
            (eachInstitute) => eachInstitute.institute_id !== 'INST_OTHER'
          );
        }
      }
    }
    const otherOption = filterText
      ? [
          {
            institute_id: 'Other',
            institute_name: filterText,
            institute_type: 'Other'
          }
        ]
      : [];

    const instituteItems = [...json.institutes, ...otherOption];

    institutes.current = instituteItems;

    return {
      items: instituteItems,
      cursor: null,
      filterText: filterText
    };
  };

  const list = useAsyncList({
    load: loadItems,
    initialFilterText: initialInstituteValue ?? '',
    initialSelectedKeys: [],
    getKey: (item: Institute) => item.institute_id
  });

  const getInputTextFromValue = (value, list) => {
    return getInstituteNameFromInstituteId(value, list);
  };

  const getValueFromInputText = (text, list) => {
    return getInstituteIdFromInstituteName(text, list);
  };

  return (
    <ComboBox
      {...others}
      selectedKey={selectedKey}
      onSelectionChange={onSelectionChangeFunc}
      onInputChange={onInputChangeFunc}
      onOpenChange={onOpenChangeFunc}
      inputFieldStyles={cn('w-full', inputFieldStyles)}
      buttonStyles={buttonStyles}
      getListItemKey={(item) => item.institute_id}
      placeholder={placeholder}
      getListItemTextValue={(item) => item.institute_name}
      renderListItem={({ item }) => {
        let instituteName = item.institute_name;
        if (item.institute_id === 'Other' && !!instituteName) {
          instituteName = `Create Option "${instituteName}"`;
        }
        return <p key={item.institute_id}>{instituteName}</p>;
      }}
      error={error}
      getInputTextFromValue={getInputTextFromValue}
      getValueFromInputText={getValueFromInputText}
      initialValue={initialInstituteValue}
      list={list}
      getDisplayValueFromSelectedState={(item) => {
        return item.institute_name;
      }}
      mobilePlaceholder={mobilePlaceholder}
      enableDataMask={enableDataMask}
      hintClassName={hintClassName}
    />
  );
};
